import { useRef, useState, useEffect } from "react";
import { Chip } from "@mui/material";
import { ReactComponent as ClearRoundedIcon } from "@/assets/svgs/x-mark.svg";
import PropTypes from "prop-types";
import classname from "classnames";
import InputWithTooltipPlaceholder from './InputWithTooltipPlaceholder';
import { cleanDescription } from '@/utils/clean-description';

function getClassName(error) {
    return classname({
        "eform-input": true,
        "eform-input-standard": true,
        "eform-input-error": error,
    });
}

const CellChipComponent = ({
    error,
    isReadOnly,
    schema,
    getFieldValue,
    payload,
    parentProps,
    inputProps,
    setInputProps,
    updatePayload,
    validateSchema,
}) => {
    const [chips, setChips] = useState([]);
    const [inputValue, setInputValue] = useState("");
    const inputRef = useRef();

    useEffect(() => {
        const fieldValue = getFieldValue(payload, parentProps);
        const chipValues = Array.isArray(fieldValue) ? fieldValue.filter((v) => v !== null && v !== undefined && v !== "") : [];
        const valueProp = fieldValue !== null && fieldValue !== undefined && fieldValue !== "" ? { value: fieldValue } : {};

        setInputProps({
            ...inputProps,
            ...valueProp,
            placeholder: cleanDescription(schema.description) || schema.title || "",
            type: Array.isArray(schema.items?.type) ? schema.items?.type?.find((d) => d !== "null") : schema.items?.type,
            value: chipValues,
        });
        setChips(chipValues);
    }, [payload, isReadOnly]);

    const handleKeyDown = (e) => {
        if (e.key === "Backspace" && inputValue === "" && chips?.length > 0) {
            removeChip(chips?.length - 1);
            return;
        }

        if (e.key !== "Enter") return;

        e.preventDefault();
        let value = inputValue.trim();

        if (value === "" ) {
            setInputProps({ ...inputProps});
            validateSchema(null);
            setInputValue("");
            return;
        }

        value = convertValue(inputProps.type, value);
        if (value === undefined) {
            validateSchema([...chips, value]);
            setInputValue("");
            return;
        }

        const newChips = [...chips, value];

        setChips(newChips);
        setInputProps({ ...inputProps, value: newChips });
        updatePayload(newChips);
        validateSchema(newChips);
        setInputValue("");
    };

    const handleInputBlur = () => {
        let value = inputValue.trim();

        if (value === "") {
            setInputProps({ ...inputProps });
            validateSchema(chips.length > 0 ? chips : null);
            setInputValue("");
            return;
        }

        value = convertValue(inputProps.type, value);
        if (value === undefined) {
            validateSchema(chips);
            setInputValue("");
            return;
        }

        const newChips = [...chips, value];
        setChips(newChips);
        setInputProps({ ...inputProps, value: newChips });
        updatePayload(newChips);
        validateSchema(newChips);
        setInputValue("");
    };

    const convertValue = (type, value) => {
        const numberRegex = /^-?\d+(\.\d+)?$/;
        if (type === "number") {
            return numberRegex.test(value) ? Number(value) : undefined;
        } else if (type === "string") {
            return isNaN(value) ? String(value) : undefined;
        }
    };

    const handleInputChange = (e) => {
        setInputValue(e.target.value);
    };

    const removeChip = (indexToRemove) => {
        const filteredChips = chips?.filter((_, index) => index !== indexToRemove);
        setChips(filteredChips);
        updatePayload(filteredChips);
        validateSchema(filteredChips.length > 0 ? filteredChips : null);
    };

    return (
        <div className="chip-input-wrapper">
            <div className={`${getClassName(error)} chip-list-wrapper ${isReadOnly ? 'disabled' : ''}`} style={{ height: "fit-content" }}>
                {(isReadOnly ? inputProps?.value || [] : chips)?.map((chip, index) => (
                    <Chip
                        key={index}
                        label={chip}
                        {...(!isReadOnly && {
                            onDelete: () => removeChip(index),
                            deleteIcon: <ClearRoundedIcon className="chip-item-delete-icon" data-testid="chip-delete-icon" />,
                        })}
                        {...(isReadOnly && {
                            sx: {
                                borderColor: "#60636B" ,
                                border: 1,
                                fontWeight:"fontWeightMedium" ,
                            }
                        })}
                        className="chip-item"
                        data-testid={`chip-item-${index}`}
                        variant="outlined"
                    />
                ))}
                {!isReadOnly && (
                    <InputWithTooltipPlaceholder
                        ref={inputRef}
                        type="text"
                        disabled={isReadOnly}
                        data-testid="chip-input-field"
                        className="chip-input-field"
                        value={inputValue}
                        onKeyDown={isReadOnly ? null : handleKeyDown}
                        onChange={isReadOnly ? null : handleInputChange}
                        onBlur={isReadOnly ? null : handleInputBlur}
                        placeholder={!isReadOnly && chips?.length === 0 ? inputProps?.placeholder : ""}
                    />
                )}
            </div>
        </div>
    );
};

CellChipComponent.propTypes = {
    error: PropTypes.any,
    isReadOnly: PropTypes.any,
    schema: PropTypes.any,
    payload: PropTypes.any,
    parentProps: PropTypes.any,
    inputProps: PropTypes.any,
    setInputProps: PropTypes.any,
    updatePayload: PropTypes.any,
    validateSchema: PropTypes.any,
    getFieldValue: PropTypes.any,
};

export default CellChipComponent;
